import React from 'react'
import { STORAGE_KEYS } from '../lib/constants'
import { getItem, setItem } from '../lib/storage'
import { reportError } from '../lib/reporting'

const VISUAL_FIXTURES = process.env.EXPO_PUBLIC_VISUAL_FIXTURES === '1'

/** Remember the player's last report choice across attacks, phases and games. */
export function useAttackLogExpanded(viewerUserId?: string): [boolean, () => void] {
  const key = viewerUserId ? `${STORAGE_KEYS.ATTACK_LOG_EXPANDED}_${viewerUserId}` : null
  const [choice, setChoice] = React.useState({ key, expanded: false })
  const edited = React.useRef(false)
  const writes = React.useRef(Promise.resolve())
  if (choice.key !== key) {
    setChoice({ key, expanded: false })
    edited.current = false
  }
  React.useEffect(() => {
    let active = true
    if (key && !VISUAL_FIXTURES) {
      getItem(key).then(stored => {
        // A late storage read must never undo a tap made while loading.
        if (active && !edited.current) setChoice({ key, expanded: stored === 'true' })
      }).catch((error: unknown) => {
        reportError(error, { tags: { area: 'attack-log', action: 'load-preference' } })
      })
    }
    return () => { active = false }
  }, [key])
  const expanded = choice.key === key && choice.expanded
  const toggle = React.useCallback(() => {
    const next = !expanded
    edited.current = true
    setChoice({ key, expanded: next })
    if (key && !VISUAL_FIXTURES) {
      // Serialize rapid taps so an older native write cannot win last.
      writes.current = writes.current.then(() => setItem(key, String(next))).catch((error: unknown) => {
        reportError(error, { tags: { area: 'attack-log', action: 'save-preference' } })
      })
    }
  }, [key, expanded])
  return [expanded, toggle]
}
